import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { fetchProductById, Product } from "../services/productService";
import toast from "react-hot-toast";

const WHATSAPP_NUMBER = "2348123456789";
const PHONE_NUMBER = "+234 812 345 6789";

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentTenant } = useSelector((state: RootState) => state.tenant);
  const [product, setProduct] = React.useState<Product | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id || !currentTenant?.id) return;
      setLoading(true);
      setError(null);
      try {
        const prod = await fetchProductById(currentTenant.id, id);
        setProduct(prod);
      } catch (err: any) {
        setError(
          err.response?.data?.detail || err.message || "Failed to fetch product"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, currentTenant]);

  const handleWhatsAppOrder = () => {
    if (!product) return;
    const msg = encodeURIComponent(
      `Hello, I want to order the following product:\n\nProduct: ${
        product.name
      }\nPrice: ₦${product.price_per_unit.toLocaleString()} per ${
        product.unit
      }\nPlease provide a quote and availability.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, "_blank");
  };

  const handleCall = () => {
    navigator.clipboard.writeText(PHONE_NUMBER);
    toast.success("Phone number copied! Call us to place your order.");
  };

  const handleLiveChat = () => {
    toast("Live chat coming soon!");
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-green-600 font-semibold">
        Loading product...
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-12 text-red-600 font-semibold">
        {error}
      </div>
    );
  }
  if (!product) {
    return (
      <div className="text-center py-12 text-gray-500">Product not found.</div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="flex flex-col md:flex-row gap-8">
        <div className="md:w-1/2">
          <div className="bg-gray-100 rounded-lg flex items-center justify-center h-80 mb-4 overflow-hidden">
            {product.product_images && product.product_images.length > 0 ? (
              <img
                src={product.product_images[0]}
                alt={product.name}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <div className="text-7xl text-green-400">🥕</div>
            )}
          </div>
          {/* Thumbnails */}
          {product.product_images && product.product_images.length > 1 && (
            <div className="flex gap-2 mt-2">
              {product.product_images.slice(1, 5).map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={product.name + " thumb " + idx}
                  className="w-16 h-16 object-cover rounded border"
                />
              ))}
            </div>
          )}
        </div>
        <div className="md:w-1/2 flex flex-col gap-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {product.name}
          </h1>
          <div className="text-lg text-gray-700 mb-2">
            {product.description}
          </div>
          <div className="flex items-center gap-4 mb-2">
            <span className="text-green-700 font-semibold text-2xl">
              ₦{product.price_per_unit.toLocaleString()}
            </span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
              {product.unit}
            </span>
            {product.is_available ? (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                In Stock
              </span>
            ) : (
              <span className="text-xs bg-gray-200 text-gray-500 px-2 py-1 rounded">
                Out of Stock
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500 mb-2">
            <span className="font-semibold">Minimum Order:</span>{" "}
            {product.minimum_order_quantity}
          </div>
          <div className="text-sm text-gray-500 mb-2">
            <span className="font-semibold">Current Stock:</span>{" "}
            {product.current_stock}
          </div>
          {product.quality_grade && (
            <div className="text-sm text-gray-500 mb-2">
              <span className="font-semibold">Quality Grade:</span>{" "}
              {product.quality_grade}
            </div>
          )}
          {product.farm_id && (
            <div className="text-sm text-gray-500 mb-2">
              <span className="font-semibold">Farm ID:</span> {product.farm_id}
            </div>
          )}
          <div className="flex flex-col gap-3 mt-6">
            <button
              className="btn-primary px-6 py-3 text-lg"
              onClick={handleWhatsAppOrder}
            >
              Order via WhatsApp
            </button>
            <button
              className="border border-green-500 text-green-700 px-6 py-3 rounded-lg text-lg hover:bg-green-50 transition-colors"
              onClick={handleCall}
            >
              Call to Order ({PHONE_NUMBER})
            </button>
            <button
              className="border border-blue-500 text-blue-700 px-6 py-3 rounded-lg text-lg hover:bg-blue-50 transition-colors"
              onClick={handleLiveChat}
            >
              Live Chat (Coming Soon)
            </button>
          </div>
        </div>
      </div>
      {/* Additional Info */}
      {(product["nutritional_info"] || (product as any).nutritionalInfo) && (
        <div className="mt-8">
          <h2 className="text-xl font-bold mb-2">Nutritional Information</h2>
          <pre className="bg-gray-50 rounded p-4 text-sm text-gray-700 overflow-x-auto">
            {JSON.stringify(
              product["nutritional_info"] || (product as any).nutritionalInfo,
              null,
              2
            )}
          </pre>
        </div>
      )}
      {(product["storage_conditions"] ||
        (product as any).storageConditions) && (
        <div className="mt-4 text-sm text-gray-500">
          <span className="font-semibold">Storage Conditions:</span>{" "}
          {product["storage_conditions"] || (product as any).storageConditions}
        </div>
      )}
      {(product["harvest_date"] || (product as any).harvestDate) && (
        <div className="mt-2 text-sm text-gray-500">
          <span className="font-semibold">Harvest Date:</span>{" "}
          {new Date(
            product["harvest_date"] || (product as any).harvestDate
          ).toLocaleDateString()}
        </div>
      )}
      {(product["expiry_date"] || (product as any).expiryDate) && (
        <div className="mt-2 text-sm text-gray-500">
          <span className="font-semibold">Expiry Date:</span>{" "}
          {new Date(
            product["expiry_date"] || (product as any).expiryDate
          ).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
